/**
 * Every page the site publishes, in one list.
 *
 * Three things used to be able to disagree with each other: the routes in
 * App.tsx, the URLs in sitemap.xml, and the single title index.html gave to all
 * of them. This list is now the source for the HTML the build prerenders, the
 * sitemap it writes, and the title and description each page carries in both
 * the prerendered file and the browser after a client-side navigation.
 *
 * Adding a route to App.tsx without adding it here does not half-work: no HTML
 * file is written for the path, and Cloudflare Pages answers it with the 404.
 * That is the intended failure — loud, on the first click — rather than a page
 * that quietly shares the front page's title forever.
 */
import { helpPath as helpPathFor, SITE_ORIGIN } from "@gen/core/links.ts";
import { BOOKS, type Book } from "../data/books.ts";

export { SITE_ORIGIN };

const PRESS = "Wise Hyena Press";

const HOME_DESCRIPTION =
  "An independent press publishing books that are checked before they are printed. Currently: logic grid puzzles.";

export interface Page {
  /** Rooted path, no trailing slash. "/" for the front page. */
  path: string;
  title: string;
  description: string;
  /**
   * The path search engines should treat as the original, when this one is a
   * second address for a page that already exists. Defaults to `path`.
   */
  canonical?: string;
  /** Whether the path belongs in sitemap.xml. */
  indexed: boolean;
  /**
   * The share card, rooted. Defaults to the press's own card — which is right
   * for every page that is about the press, and wrong for a book, where the
   * thing being shared is the book. site/scripts/brand-rasters.ts draws both.
   */
  image?: string;
  /**
   * schema.org description of what this page is about, as a JSON-LD object.
   * Prerendered into the file so a crawler reads it without running anything.
   */
  schema?: object;
}

/** The card used by anything that has not named its own. */
const DEFAULT_IMAGE = "/og.png";

export function imageUrl(page: Page): string {
  return absoluteUrl(page.image ?? DEFAULT_IMAGE);
}

/** The press itself, referenced by every other schema rather than repeated. */
const PUBLISHER = {
  "@type": "Organization",
  "@id": `${SITE_ORIGIN}/#press`,
  name: PRESS,
  url: `${SITE_ORIGIN}/`,
  logo: `${SITE_ORIGIN}/apple-touch-icon.png`,
  description: HOME_DESCRIPTION,
  email: "hello@wisehyena.com",
};

/** A path as an absolute URL, which is what canonical and og:url both want. */
export function absoluteUrl(path: string): string {
  return path === "/" ? `${SITE_ORIGIN}/` : SITE_ORIGIN + path;
}

/** Where this page says it really lives. */
export function canonicalUrl(page: Page): string {
  return absoluteUrl(page.canonical ?? page.path);
}

function bookPage(book: Book): Page {
  const name = book.subtitle ? `${book.title}: ${book.subtitle}` : book.title;
  const url = absoluteUrl(`/catalogue/${book.slug}`);
  return {
    path: `/catalogue/${book.slug}`,
    title: `${name} · ${PRESS}`,
    description: book.blurb,
    indexed: true,
    image: `/og/${book.slug}.png`,
    /**
     * Only what the record actually knows. No price, because the site does not
     * set one — Amazon does, and a stale number in structured data is worse
     * than none. No ISBN, because books.ts does not carry one yet. Everything
     * here is a fact the press could be held to.
     */
    schema: {
      "@context": "https://schema.org",
      "@type": "Book",
      name,
      url,
      description: book.blurb,
      image: absoluteUrl(`/covers/${book.slug}-640.png`),
      bookFormat: "https://schema.org/Paperback",
      inLanguage: "en",
      numberOfPages: book.pages,
      datePublished: book.published,
      publisher: PUBLISHER,
      author: PUBLISHER,
      // Claimed only when there is somewhere to buy it. A book the press has
      // archived but not yet listed is not in stock anywhere, and saying it is
      // would be a false claim in structured data.
      ...(book.asin
        ? {
            offers: {
              "@type": "Offer",
              url: `https://www.amazon.com/dp/${book.asin}`,
              availability: "https://schema.org/InStock",
            },
          }
        : {}),
    },
  };
}

/**
 * One book's own help address, which is what gets printed into that book.
 *
 * A code in the front of a book knows which book it is in, so the reader
 * should never be asked. `/help` still exists for somebody who arrives without
 * one and has to say which book they are holding.
 *
 * Out of the index, pointing at `/help`. With one archived book these two
 * pages are nearly the same document, and the per-book one is a printed
 * shortcut rather than something anybody should reach by searching. `/help` is
 * the page search engines should hold.
 */
function helpPage(book: Book): Page {
  const name = book.subtitle ? `${book.title}: ${book.subtitle}` : book.title;
  return {
    path: helpPath(book),
    title: `Puzzle help: ${name} · ${PRESS}`,
    description: `Stuck on a puzzle in ${name}? Tell us where you have got to and we will give you the next deduction — one step, not the solution.`,
    canonical: "/help",
    indexed: false,
  };
}

/**
 * Where a book's help lives. The path itself is built by the generator, in
 * `core/links.ts`, because it is printed into books as a QR code — this is the
 * one address the press cannot change once it is on paper. Importing it rather
 * than restating it is what stops the printed code and the served route from
 * ever disagreeing.
 */
export function helpPath(book: Book): string {
  return helpPathFor(book.slug);
}

export const PAGES: Page[] = [
  {
    path: "/",
    title: PRESS,
    description: HOME_DESCRIPTION,
    indexed: true,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        PUBLISHER,
        {
          "@type": "WebSite",
          url: `${SITE_ORIGIN}/`,
          name: PRESS,
          description: HOME_DESCRIPTION,
          publisher: { "@id": `${SITE_ORIGIN}/#press` },
        },
      ],
    },
  },
  {
    path: "/catalogue",
    title: `Catalogue · ${PRESS}`,
    description:
      "Every book Wise Hyena Press has in print, grouped by line. A book joins the list once it has been checked, not once it has been written.",
    indexed: true,
  },
  ...BOOKS.map(bookPage),
  {
    path: "/logic-grids",
    title: `Logic grid puzzles · ${PRESS}`,
    description:
      "Logic grid puzzles with three promises checked on every puzzle: exactly one solution, reachable without guessing, and no clue you could remove.",
    indexed: true,
  },
  {
    path: "/help",
    title: `Puzzle help · ${PRESS}`,
    description:
      "Stuck on a puzzle from one of our books? Tell us where you have got to and we will give you the next deduction — one step, not the solution.",
    indexed: true,
  },
  ...BOOKS.filter((b) => b.archived).map(helpPage),
];

/**
 * The page a path gets when nothing matches. Prerendered to dist/404.html,
 * which is what Cloudflare Pages serves — with a real 404 status — for a path
 * that has no file.
 */
export const NOT_FOUND: Page = {
  path: "/404",
  title: `Not found · ${PRESS}`,
  description: HOME_DESCRIPTION,
  indexed: false,
};

export function pageFor(pathname: string): Page {
  return PAGES.find((p) => p.path === pathname) ?? NOT_FOUND;
}

function tag(attr: "name" | "property", key: string, content: string): void {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = content;
}

/**
 * Bring the document head into line with the page being shown.
 *
 * The prerendered file already carries the right tags on a cold load; this is
 * for what happens afterwards, when a client-side navigation swaps the page
 * under a head that still describes the last one.
 */
export function applyMeta(page: Page): void {
  const url = canonicalUrl(page);
  document.title = page.title;
  tag("name", "description", page.description);
  tag("property", "og:title", page.title);
  tag("property", "og:description", page.description);
  tag("property", "og:url", url);
  // Every page names a card, so this never has to clear one: navigating from a
  // book to the catalogue swaps the book's cover back to the press's card
  // rather than leaving the last book's art on the wrong page.
  tag("property", "og:image", imageUrl(page));

  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (page.path === NOT_FOUND.path) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  link.href = url;
}
